import { Check, Circle, Loader2, XCircle, ChevronDown, ChevronRight, ListTodo } from "lucide-react";
import { useState } from "react";
import type { PlanStep } from "../types";

interface PlanBlockProps {
  steps: PlanStep[];
}

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; spin?: boolean }> = {
  pending:       { icon: Circle,  color: "text-muted-foreground/50" },
  "in-progress": { icon: Loader2, color: "text-blue-500", spin: true },
  complete:      { icon: Check,   color: "text-green-500" },
  failed:        { icon: XCircle, color: "text-red-500" },
};

function PlanStepRow({ step, depth = 0 }: { step: PlanStep; depth?: number }) {
  const [isOpen, setIsOpen] = useState(true);
  const config = STATUS_CONFIG[step.status] ?? STATUS_CONFIG.pending;
  const Icon = config.icon;
  const hasSubsteps = step.substeps && step.substeps.length > 0;

  return (
    <div>
      <div
        className="flex items-start gap-2 py-1"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        {hasSubsteps ? (
          <button
            type="button"
            className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => setIsOpen((o) => !o)}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="mt-0.5 shrink-0 w-3.5" />
        )}

        <Icon
          className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${config.color} ${config.spin ? "animate-spin" : ""}`}
        />

        <div className="min-w-0 flex-1">
          <p className="text-sm">{step.title}</p>
          {step.description && (
            <p className="text-xs text-muted-foreground">{step.description}</p>
          )}
        </div>
      </div>

      {hasSubsteps && isOpen && (
        <div>
          {step.substeps!.map((sub) => (
            <PlanStepRow key={sub.id} step={sub} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PlanBlock({ steps }: PlanBlockProps) {
  return (
    <div className="my-2 rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <ListTodo className="h-4 w-4" />
        Execution Plan
      </div>
      <div className="space-y-0.5">
        {steps.map((step) => (
          <PlanStepRow key={step.id} step={step} />
        ))}
      </div>
    </div>
  );
}
